import { useQuery, ApolloError } from '@apollo/client'
import { CHECK_FOR_CHAT_HISTORY } from 'share/queries/chat'
import { ChatHistory, ChatHistoryResponse } from 'share/domain/chat'
import { MessageType, CaseId } from './types'
import { logger } from './logger'

// ---------- Conversation ID Utilities ----------

/**
 * Validates if a conversation ID matches the expected UUID v4 format
 *
 * @param id - The conversation ID to validate
 * @returns Boolean indicating if the ID matches UUID v4 format
 */
export function validateConversationIdFormat(id: string): boolean {
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const uuidV4Regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidV4Regex.test(id)
}

// ---------- Message Type Utilities ----------

/**
 * Maps server message types to UI message types for consistent rendering
 *
 * The server sends messages with various type strings, but our UI only needs
 * to know if it's from the user, bot, or system for styling/behavior purposes.
 *
 * @param msg - Message object with a type property
 * @returns Normalized message type for UI components
 */
export function getMessageType(msg: { readonly type: string }): MessageType {
  // Normalize to uppercase for case-insensitive comparison
  const serverType = msg.type?.toUpperCase?.() ?? ''

  // System messages (errors, notifications) get special styling
  if (serverType === 'SYSTEM') return MessageType.SYSTEM

  // Default: USER messages from client, everything else from bot
  return serverType === 'USER' ? MessageType.USER : MessageType.BOT
}

// ---------- Session Management ----------

/**
 * Parses and validates case ID from URL route parameter
 *
 * Converts string route parameter to integer with validation.
 * If no case ID is provided or it's invalid, falls back to default case ID 456.
 * This ensures the chat functionality always has a valid case context.
 *
 * @param caseIdParam - Route parameter string (e.g., "123" from "/case/123")
 * @returns Parsed integer case ID or fallback case ID 123
 */
export function parseCaseId(caseIdParam: string | undefined): CaseId {
  const FALLBACK_CASE_ID = 123

  if (!caseIdParam) {
    logger.info('No case ID in URL, using fallback', {
      fallbackCaseId: FALLBACK_CASE_ID,
      reason: 'caseIdParam is undefined'
    })
    return FALLBACK_CASE_ID
  }

  // Parse string to integer using base 10
  const parsedCaseId = Number.parseInt(caseIdParam, 10)

  // Validate result is a finite positive number
  if (!Number.isFinite(parsedCaseId) || parsedCaseId <= 0) {
    logger.warn('Invalid case ID provided, using fallback', {
      caseIdParam,
      parsedCaseId,
      fallbackCaseId: FALLBACK_CASE_ID,
      reason: 'Not a finite positive number'
    })
    return FALLBACK_CASE_ID
  }

  return parsedCaseId
}

// ---------- Chat History ----------

/**
 * Validates if chat history data contains any messages
 *
 * Performs null-safe check on GraphQL response data to determine
 * if previous conversation history exists for the current case.
 *
 * @param data - GraphQL response containing chatHistory array
 * @returns Boolean indicating presence of chat history
 */
export const hasChatHistory = (
  data: ChatHistoryResponse | undefined
): boolean => {
  // Double negation (!!) converts truthy/falsy to strict boolean
  return !!(data?.chats?.chatHistory && data.chats.chatHistory.length > 0)
}

/**
 * Safely extracts chat history array from GraphQL response
 *
 * Provides null-safe access to chatHistory array with fallback
 * to empty array for consistent return type handling.
 *
 * @param data - GraphQL response that may contain chatHistory
 * @returns Array of chat history messages (empty if none exist)
 */
export const getChatHistory = (
  data: ChatHistoryResponse | undefined
): ChatHistory[] => {
  // Logical OR provides fallback to empty array for null/undefined data
  return data?.chats?.chatHistory || []
}

/**
 * Custom React hook for fetching and managing chat history state
 *
 * Encapsulates GraphQL query logic for retrieving conversation history
 * with loading states, error handling, and computed derived state.
 * Uses Apollo's cache-and-network policy for optimal UX.
 *
 * @param caseId - Case identifier for scoping chat history query
 * @returns Object containing history data, loading state, errors, and computed flags
 */
export const useChatHistory = (
  caseId: number
): {
  data: ChatHistory[] // Processed array of historical messages
  loading: boolean // Query loading state
  error: ApolloError | undefined // GraphQL/network errors
  hasHistory: boolean // Computed flag for conditional rendering
} => {
  // Execute GraphQL query with Apollo Client
  const { data, loading, error } = useQuery<ChatHistoryResponse>(
    CHECK_FOR_CHAT_HISTORY,
    {
      variables: { caseId: String(caseId) }, // Query variables for server
      skip: !caseId, // Conditional execution based on caseId presence
      fetchPolicy: 'cache-and-network' // Cache-first with network fallback
    }
  )

  // Transform raw GraphQL response into component-friendly format
  const history = getChatHistory(data)
  const hasHistory = hasChatHistory(data)

  return {
    data: history, // Processed message array for rendering
    loading, // Boolean loading state for UI feedback
    error, // Apollo error object for error boundaries
    hasHistory // Convenience flag for conditional logic
  }
}
